/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Text from 'src/components/ts-components/Text'
import { useScrollSpyContext } from './ScrollSpyContext'

interface SidebarProps {
  /** Sidebar width in px. Default 320 (Figma). */
  width?: number
  /** Accessible label for the nav landmark. */
  ariaLabel?: string
}

// SECONDARY_350 is dropped from the inferred type of the JS appColors module,
// so read it through a narrow string-record cast (same pattern used elsewhere).
const palette = colors as Record<string, string>

const Sidebar = ({ width = 320, ariaLabel = 'Sections' }: SidebarProps) => {
  const { activeId, items, scrollToSection } = useScrollSpyContext()
  return (
    <nav css={containerStyle(width)} aria-label={ariaLabel}>
      {items.map((item) => {
        const isActive = item.key === activeId
        const Icon = item.icon
        return (
          <button
            key={item.key}
            type='button'
            css={itemStyle(isActive)}
            aria-current={isActive ? 'location' : undefined}
            onClick={() => scrollToSection(item.key)}
          >
            {isActive && <span css={accentBarStyle} aria-hidden='true' />}
            {Icon ? (
              <span css={iconStyle(isActive)} aria-hidden='true'>
                <Icon />
              </span>
            ) : null}
            <Text
              variant='paragraphSmall'
              weight={isActive ? 'medium' : 'regular'}
              color={isActive ? colors.PRIMARY : colors.NEUTRAL_0}
            >
              {capitalizeWords(item.label)}
            </Text>
          </button>
        )
      })}
    </nav>
  )
}

export default Sidebar

// ---- styled ----
const containerStyle = (width: number) => css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: ${width}px;
  min-width: ${width}px;
  /* The layout clips at its own height, so a list taller than the viewport
     (a short screen, or many sections) must scroll on its own. */
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
`

const itemStyle = (isActive: boolean) => css`
  position: relative;
  height: 40px;
  /* Keep the 40px rows when the list overflows and scrolls. */
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 16px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  background-color: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_500};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${isActive
      ? colors.SECONDARY_300
      : palette.SECONDARY_350};
  }
`

const iconStyle = (isActive: boolean) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: ${isActive ? colors.PRIMARY : colors.NEUTRAL_0};
`

const accentBarStyle = css`
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 4px;
  border-radius: 2px;
  background-color: ${colors.PRIMARY};
`
